import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { categoryIdForPath, isSamePath, searchAdminPages } from '@/constants/adminNavCategories';

/**
 * The admin pages on desktop: one "Workspace" tab in the top bar that opens a
 * console-style panel (the way AWS or Google lay out their consoles): a
 * search field, then every area side by side with its pages listed under it.
 * Same areas and the same search as the mobile drawer (AdminNavCategories).
 *
 * The search field takes focus when the panel opens, so typing a page name
 * and pressing a link is the fastest path to anything.
 */
export default function AdminNavMegaMenu({ categories, pathname, triggerClassName = '' }) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const activeId = categoryIdForPath(categories, pathname);
  const matches = searchAdminPages(categories, query);

  const onOpenChange = (next) => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const pageLink = (item, { category = null } = {}) => {
    const active = isSamePath(item.url, pathname);
    return (
      <li key={`${category || ''}${item.url}`}>
        <Link
          to={item.url}
          onClick={() => onOpenChange(false)}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-[13px] font-normal transition-colors focus:outline-none focus-visible:bg-white/[0.08]',
            active ? 'bg-white/[0.08] text-white' : 'text-white/80 hover:bg-white/[0.07] hover:text-white',
          )}
        >
          <item.icon className={cn('w-4 h-4 flex-shrink-0', active ? 'text-[hsl(228,35%,68%)]' : 'text-white/40')} />
          <span className="truncate">{item.title}</span>
          {category && <span className="ml-auto pl-3 text-[11px] text-white/35 whitespace-nowrap">{category}</span>}
        </Link>
      </li>
    );
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'text-sm transition-all duration-300 px-4 py-1.5 h-auto rounded-full flex items-center gap-1.5',
            activeId || open ? 'glass-light' : 'hover:bg-white/10 text-gray-300 hover:text-white',
            triggerClassName,
          )}
        >
          Workspace
          <ChevronDown className={cn('w-3 h-3 transition-transform duration-200', open && 'rotate-180', activeId ? 'text-white/60' : 'text-gray-500')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="center"
        sideOffset={14}
        collisionPadding={16}
        className="w-[min(calc(100vw-32px),760px)] p-0 overflow-hidden rounded-2xl border-white/10 bg-black/90 backdrop-blur-2xl text-white shadow-[0_24px_64px_-16px_rgba(0,0,0,0.9)]"
      >
        <div className="relative p-3 border-b border-white/[0.08]">
          <Search className="pointer-events-none absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Find an admin page"
            aria-label="Find an admin page"
            className="w-full h-9 rounded-lg bg-white/[0.04] border border-white/[0.08] pl-9 pr-9 text-[13px] font-normal text-white placeholder:text-white/35 focus:outline-none focus:border-white/20 [&::-webkit-search-cancel-button]:hidden"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="absolute right-5 top-1/2 -translate-y-1/2 p-1 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {query.trim() ? (
          <div className="max-h-[min(60vh,480px)] overflow-y-auto p-2">
            {matches.length > 0 ? (
              <ul aria-label="Matching admin pages">{matches.map(({ item, category }) => pageLink(item, { category }))}</ul>
            ) : (
              <p className="px-2 py-3 text-[13px] font-normal text-white/45">No admin page matches “{query.trim()}”.</p>
            )}
          </div>
        ) : (
          <div className="max-h-[calc(100vh-140px)] overflow-y-auto p-3 grid grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-4">
            {categories.map((c) => (
              <section key={c.id} aria-labelledby={`admin-nav-${c.id}`}>
                <h3 id={`admin-nav-${c.id}`} className="flex items-center gap-2 px-2 pb-1.5 text-[11px] uppercase tracking-[0.16em] font-medium text-white/45">
                  <span className={cn(
                    'inline-flex items-center justify-center w-5 h-5 rounded-md ring-1 ring-inset',
                    c.id === activeId ? 'bg-[hsl(228,45%,30%)]/40 ring-[hsl(228,45%,55%)]/30' : 'bg-white/[0.05] ring-white/[0.06]',
                  )}>
                    <c.icon className={cn('w-3 h-3', c.id === activeId ? 'text-[hsl(228,45%,78%)]' : 'text-white/55')} />
                  </span>
                  {c.title}
                </h3>
                <ul>{c.items.map((item) => pageLink(item))}</ul>
              </section>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

AdminNavMegaMenu.propTypes = {
  categories: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
    })).isRequired,
  })).isRequired,
  pathname: PropTypes.string,
  triggerClassName: PropTypes.string,
};
